import * as pdfjsLib from 'pdfjs-dist';

// 【重要】PDF.jsは重い処理をバックグラウンドで行うためのWorkerが必要です。
// cdnjsではmjsファイルが404になる場合があるため、jsDelivrを使用
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

export default function initPdfViewer() {
  // 自動的にページ内のPDFを読み込んでいるiframeを取得します
  // src属性に「.pdf」が含まれるiframe、または特定のクラス「.js-pdf-iframe」を持つものを対象とします
  const iframes = document.querySelectorAll('iframe[src*=".pdf"], iframe.js-pdf-iframe');

  if (iframes.length === 0) return;

  iframes.forEach(iframe => {
    // iframeのsrc（＝PDFのURL）を取得
    const pdfUrl = iframe.getAttribute('src');
    if (!pdfUrl) return;

    let parent = iframe.parentElement;

    // WordPressのwpautopなどでpタグに囲まれている場合、pタグだとブロック要素のレイアウトが崩れるためdivに変換するか、pの外に出す
    if (parent && parent.tagName.toLowerCase() === 'p') {
      const divWrapper = document.createElement('div');
      divWrapper.className = 'pdf-container-wrapper';
      divWrapper.style.width = '100%'; // 親がインラインブロック扱いにならないよう全体幅を確保
      parent.parentNode.insertBefore(divWrapper, parent);
      
      // pの中身（iframeなど）をdivに移動
      while (parent.firstChild) {
        divWrapper.appendChild(parent.firstChild);
      }
      parent.parentNode.removeChild(parent);
      parent = divWrapper; // 以後の親要素は新しいdiv
    }

    // PDF描画用のCanvasを新しく作成
    const canvas = document.createElement('canvas');
    canvas.className = 'js-generated-pdf-canvas';
    // 横幅を100%に保つことで、他の画像と同様にレスポンシブな表示にする
    canvas.style.display = 'block';
    canvas.style.width = '100%';
    canvas.style.height = 'auto';
    
    // ポップアップ用にPDFのURLをデータ属性として付与
    canvas.dataset.pdfUrl = pdfUrl;

    // iframeの直前にCanvasを挿入
    parent.insertBefore(canvas, iframe);
    // 元のiframeは完全にDOMから削除するか非表示にする（Mac/Safariのネイティブビューアを隠す）
    iframe.style.display = 'none';

    // エラー発生時もローダーを削除するよう、先にローダーとコンテナのスタイルを定義
    parent.style.position = 'relative';
    parent.style.minHeight = '300px'; 
    parent.style.background = '#f8fafc';
    
    const loader = document.createElement('div');
    loader.className = 'c-media-popup__loader';
    loader.innerHTML = '<span>PDFを描画中...</span>';
    parent.appendChild(loader);

    const renderPdf = async () => {
      try {
        // PDF.js often fails to load if URLs have # or ? at the end which are meant for the browser viewer.
        // 日本語ファイル名を含む可能性があるのでencodeURIでエンコードしておく（必要な部位のみ）
        let cleanPdfUrl = pdfUrl.split('#')[0].split('?')[0];

        // URLが壊れないように、日本語等のマルチバイト文字だけエンコードする代替手段として
        // 一旦URLオブジェクトを通して正規化させる
        try {
          const urlObj = new URL(cleanPdfUrl, window.location.origin);
          cleanPdfUrl = urlObj.href;
        } catch(e) {}

        const loadingTask = pdfjsLib.getDocument(cleanPdfUrl);
        const pdf = await loadingTask.promise;

        // 1ページ目を取得（複数ページの場合はループなどで拡張可能）
        const page = await pdf.getPage(1);

        // コンテナ（親要素）の幅を取得してリサイズ
        let containerWidth = parent.clientWidth;

        // 親要素の幅が取れない場合（非表示タブ内など）のフォールバック
        if (containerWidth === 0) {
          const attrWidth = iframe.getAttribute('width');
          if (attrWidth && !attrWidth.includes('%')) {
            containerWidth = parseInt(attrWidth);
          }
          if (!containerWidth) containerWidth = Math.min(window.innerWidth * 0.8, 800);
        }

        // 一旦デフォルトスケール(1.0)でビューポートを取得し、PDF本来の幅を確認
        const unscaledViewport = page.getViewport({ scale: 1.0 });

        // コンテナの幅に合わせて縮尺（スケール）を計算
        const scale = containerWidth / unscaledViewport.width;
        const viewport = page.getViewport({ scale: scale });

        // Canvasのサイズをビューポートに合わせる
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        // Retinaディスプレイなど高解像度への対応（任意）
        // const outputScale = window.devicePixelRatio || 1;
        // canvas.width = Math.floor(viewport.width * outputScale);
        // canvas.height = Math.floor(viewport.height * outputScale);
        // canvas.style.width = Math.floor(viewport.width) + "px";
        // canvas.style.height = Math.floor(viewport.height) + "px";
        // const transform = outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null;

        const renderContext = {
          canvasContext: context,
          viewport: viewport,
          // transform: transform // 高解像度対応時はこれもコメントを外す
        };

        // 描画実行
        await page.render(renderContext).promise;

        // 描画完了時にローダーを削除
        if (loader && loader.parentNode) {
          loader.parentNode.removeChild(loader);
          parent.style.minHeight = 'auto';
        }

      } catch (error) {
        console.error('PDFの読み込みに失敗しました:', error);
        
        // エラー発生時もローダーを削除
        const existingLoader = parent.querySelector('.c-media-popup__loader');
        if (existingLoader) {
          existingLoader.remove();
          parent.style.minHeight = 'auto';
        }

        // --- DEBUG OUTPUT ---
        const debugMsg = document.createElement('div');
        debugMsg.style.color = 'red';
        debugMsg.style.fontSize = '12px';
        debugMsg.style.margin = '10px 0';
        debugMsg.innerText = `[Debug] PDF Load Error: ${error.message || error} | URL: ${pdfUrl}`;
        parent.insertBefore(debugMsg, iframe);
        // --------------------

        // 描画失敗時は保険として元のiframeを再表示する
        iframe.style.display = 'block';
        canvas.style.display = 'none';
      }
    };

    // 画面内に表示されるまで描画を遅延させることで、正確な幅を取得し、
    // またユーザーがスクロールしてきた時にローディングと描画の様子が分かるようにする
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries, obs) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            renderPdf();
            obs.unobserve(entry.target);
          }
        });
      }, { rootMargin: '200px' });
      observer.observe(parent);
    } else {
      // IntersectionObserverに非対応のブラウザなら即時実行
      renderPdf();
    }

    // ウィンドウリサイズ時の再描画（レスポンシブ対応）
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        renderPdf();
      }, 200);
    });
  });
}
